import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { ErrorMessageComponent } from 'src/app/components/toastr-templates/error-message/error-message.component';
import { ConfigurationService } from 'src/app/services/api/configuration.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FabricService } from 'src/app/services/fabric/fabric.service';
import { DownloadService } from 'src/app/services/util/download.service';
import { ToastrService as ToastrLibService } from 'ngx-toastr';
import {
	ReasonPhrases,
	StatusCodes,
	getReasonPhrase,
	getStatusCode,
} from 'http-status-codes';
const SERVICE_NAME = 'mgmt-crvt-resources';

@Injectable({
  providedIn: 'root'
})
export class CRVTService {
  progress = 0;
  progressToast: any;

  constructor(private fabricService: FabricService, private downloadService: DownloadService,
    private httpClient: HttpClient, private configurationService: ConfigurationService,
    private authService: AuthService, private toastr: ToastrLibService) {
  }

  getCRVTClientName(clientName?: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_crvt_mgmt-token_clientname', { clientName });
  }

  downloadCRVT(blueprintId: string, instanceId: string, runId: string, filename: string, clientName?: string, testType?: string, jobId?: string,): Observable<any> {
    this.showSuccess();
    return combineLatest([this.configurationService.getConfiguration(), this.getCRVTClientName(clientName)])
    .pipe(
      mergeMap(([config, tokenResponse]) => {
        let downloadType;
        if (testType === 'sanity_test') {
          downloadType = 'sanity-results';
        } else if (testType === 'ef_test') {
          downloadType = 'eftest-results';
        } else if (testType === 'cob-results') {
          downloadType = 'cob-results';
        } else if (testType === 'rvt-results') {
          downloadType = 'rvt-results';
        } else {
          downloadType = 'test-results'
        }

      
        let downloadUrl = `${config.crvtUrl}/crvt/${downloadType}/download/`;
        
        if(jobId){
        downloadUrl += `${blueprintId}/${instanceId}/${runId}/${jobId}/${filename}`;
        }else{
        downloadUrl += `${blueprintId}/${instanceId}/${runId}/${filename}`;
        }

        const options = {
          observe: 'response' as 'events',
          responseType: 'blob' as 'json',
          headers: {Authorization: `Bearer ${tokenResponse.value}`}
        };
          


        return this.httpClient.get<HttpResponse<Blob>>(downloadUrl, options).pipe(
      
          catchError((error: HttpErrorResponse) => {
            // Handle different error types
            if (error.status === StatusCodes.OK) {
              // Handle file found
              this.showToastr(translate('CRVT.DOWNLOAD'), translate('CRVT.SUCCESS'));
            } else if (error.status === StatusCodes.NOT_FOUND) {
              // Handle file not found
              this.showToastr(translate('CRVT.FILE_NOT_FOUND'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.FILE_NOT_FOUND')));
            } else if (error.status === StatusCodes.BAD_REQUEST) {
              // Handle unauthorized access
              this.showToastr(translate('CRVT.BAD_REQUEST'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.USER_UNAUTHOURIZED')));
            } else if (error.status === StatusCodes.UNAUTHORIZED) {
              // Handle unauthorized errors
              this.showToastr(translate('CRVT.USER_UNAUTHOURIZED'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.USER_UNAUTHOURIZED')));
            } else {
              // Handle other errors
              this.showToastr(translate('CRVT.INTERNAL_SERVER_ERROR'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.ERROR')));
            }
          })
        );
        
      }),
      // tap(response => this.downloadService.download(filename, response)));
      tap(response => this.downloadService.download(response)));
  }


  downloadReleasePack(pack: string, blueprintId: string, filename: string, clientName?: string): Observable<any> {
    this.showSuccess();
    return combineLatest([this.configurationService.getConfiguration(), this.getCRVTClientName(clientName)])
    .pipe(
      mergeMap(([config, tokenResponse]) => {
        let downloadUrl = `${config.crvtUrl}/crvt/${pack}/download/`;
        downloadUrl += `${blueprintId}/${filename}`;
        const options = {
          observe: 'response' as 'events',
          responseType: 'blob' as 'json',
          headers: {Authorization: `Bearer ${tokenResponse.value}`}
        };
        // return this.httpClient.get<HttpResponse<Blob>>(downloadUrl, options);
        return this.httpClient.get<HttpResponse<Blob>>(downloadUrl, options).pipe(
          catchError((error: HttpErrorResponse) => {
            // Handle different error types
            if (error.status === StatusCodes.OK) {
              // Handle file found
              this.showToastr(translate('CRVT.DOWNLOAD'), translate('CRVT.SUCCESS'));
            } else if (error.status === StatusCodes.NOT_FOUND) {
              // Handle file not found
              this.showToastr(translate('CRVT.FILE_NOT_FOUND'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.FILE_NOT_FOUND')));
            } else if (error.status === StatusCodes.BAD_REQUEST) {
              // Handle unauthorized access
              this.showToastr(translate('CRVT.BAD_REQUEST'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.USER_UNAUTHOURIZED')));
            } else if (error.status === StatusCodes.UNAUTHORIZED) {
              // Handle unauthorized errors
              this.showToastr(translate('CRVT.USER_UNAUTHOURIZED'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.USER_UNAUTHOURIZED')));
            } else {
              // Handle other errors
              this.showToastr(translate('CRVT.INTERNAL_SERVER_ERROR'), translate('CRVT.PLEASE_CONTACT_SUPPORT'));
              return throwError(() => new Error(translate('CRVT.ERROR')));
            }
          })
        );
        
      }),
      tap(response => this.downloadService.download(response)));
  }



  showToastr(prefix?: string, suffix?: string): void {
    setTimeout(() => {
      this.toastr.show(prefix, suffix, {
        toastComponent: ErrorMessageComponent,
        toastClass: '',
        // positionClass: 'toast-top-right',
        timeOut: 2000,
        easeTime: 100
      });
    }, 1000);
  }

  showSuccess() {
   this.toastr.success(translate('CRVT.FILE_DOWNLOAD'), '' , {
    toastClass: 'custom-toast',
    timeOut: 1000,
    extendedTimeOut: 0,
    tapToDismiss: false,

  });

  }

  getViewResultsUrl(blueprintId: string, instanceId: string, runId: string, filename: string, clientName?: string, type?: string): Observable<string> {
    return this.configurationService.getConfiguration()
    .pipe(map((config) => {
      let viewUrl = `${config.crvtUrl}crvt/mgmt-results-view/`;
      let normalizedProvider = this.authService.principal.provider === 'temenoscom' ? 'temenos.com' : this.authService.principal.provider;
      viewUrl += `${normalizedProvider}/${clientName}/${type}/${blueprintId}/${instanceId}/${runId}/${filename}`;
      return viewUrl;
    }));

  }

}
